import { useEffect, useState } from "react";
import { Cake, Cloud, CloudRain, CloudSnow, CloudSun, Disc3, MicOff, PartyPopper, Quote, Sun, Timer } from "lucide-react";
import { api } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { daysSince } from "@/components/FunCorner";
import { useRandomWheel } from "@/components/RandomWheel";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { errMsg } from "@/lib/api";

const MuteSetter = ({ days, onSaved }) => {
  const [v, setV] = useState(String(days));
  const [open, setOpen] = useState(false);
  const save = (n) => api.post("/fun/mute-reset", { days: Math.max(0, Number(n) || 0) })
    .then(() => { setOpen(false); onSaved(); toast.success("Counter updated"); }).catch((e) => toast.error(errMsg(e)));
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setV(String(days)); }}>
      <PopoverTrigger asChild>
        <button type="button" className="transition-transform hover:scale-105" data-testid="fun-mute-counter">
          <Chip icon={MicOff}><b className="font-mono">{days}</b> <span className="text-muted-foreground">Days Since We Fucked Up</span></Chip>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 space-y-3" data-testid="mute-setter">
        <p className="text-sm font-semibold">Set days since we fucked up</p>
        <form onSubmit={(e) => { e.preventDefault(); save(v); }} className="flex gap-2">
          <input type="number" min={0} value={v} onChange={(e) => setV(e.target.value)} className="h-9 w-24 rounded-md border bg-background px-3 font-mono" data-testid="mute-setter-input" />
          <button className="h-9 rounded-full bg-primary px-4 text-sm font-semibold text-primary-foreground" data-testid="mute-setter-save">Set</button>
        </form>
        <button onClick={() => save(0)} className="w-full rounded-full border py-1.5 text-sm font-semibold text-red-300 hover:bg-red-500/10" data-testid="mute-setter-zero">We fucked up (back to 0)</button>
      </PopoverContent>
    </Popover>
  );
};

const wIcon = (c) => (c === 0 ? Sun : c <= 2 ? CloudSun : c <= 48 ? Cloud : c >= 71 && c <= 77 ? CloudSnow : CloudRain);

const fridayLeft = () => {
  const now = new Date(new Date().toLocaleString("en-US", { timeZone: "Europe/London" }));
  const day = now.getDay();
  if (day === 0 || day === 6 || (day === 5 && now.getHours() >= 17)) return "Weekend!";
  const target = new Date(now);
  target.setDate(now.getDate() + ((5 - day + 7) % 7));
  target.setHours(17, 0, 0, 0);
  const mins = Math.floor((target - now) / 60000);
  const d = Math.floor(mins / 1440);
  const h = Math.floor((mins % 1440) / 60);
  return d ? `${d}d ${h}h` : `${h}h ${mins % 60}m`;
};

const Chip = ({ icon: Icon, children, testid, className = "" }) => (
  <span className={`flex items-center gap-[0.4em] whitespace-nowrap rounded-full border bg-card/70 px-[0.8em] py-[0.3em] ${className}`} data-testid={testid}>
    <Icon className="h-[1em] w-[1em] shrink-0" />{children}
  </span>
);

export const FunStrip = () => {
  const { team } = useSettings();
  const [fun, setFun] = useState(null);
  const [, tick] = useState(0);
  const load = () => api.get("/fun/state").then((r) => setFun(r.data)).catch(() => {});
  const [spinWheel, wheel] = useRandomWheel(load);
  useEffect(() => {
    load();
    const a = setInterval(load, 300000);
    const b = setInterval(() => tick((n) => n + 1), 60000);
    return () => { clearInterval(a); clearInterval(b); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const w = team?.wallboard;
  if (!w || !fun) return null;
  const WIcon = fun.weather ? wIcon(fun.weather.code) : Sun;
  return (
    <div className="mb-[0.8em] space-y-[0.5em] text-[0.8em]" data-testid="wallboard-fun-strip">
      {fun.celebrations?.map((c) => (
        <div key={c.username + c.kind} className="flex items-center gap-[0.6em] rounded-[0.7em] border border-pink-400/40 bg-pink-500/10 px-[1em] py-[0.5em] font-display text-[1.2em] font-bold" data-testid={`celebration-${c.username}`}>
          {c.kind === "birthday" ? <Cake className="h-[1.1em] w-[1.1em] text-pink-300" /> : <PartyPopper className="h-[1.1em] w-[1.1em] text-amber-300" />}
          🎈 {c.kind === "birthday" ? `Happy birthday, ${c.name}!` : `Happy ${c.years}-year work anniversary, ${c.name}!`} 🎈
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-[0.5em]">
        {w.friday_countdown && <Chip icon={Timer} testid="fun-friday-countdown"><span className="text-muted-foreground">Friday 5pm in</span> <b className="font-mono">{fridayLeft()}</b></Chip>}
        {w.weather && fun.weather && <Chip icon={WIcon} testid="fun-weather"><b className="font-mono">{fun.weather.temp}°C</b> <span className="text-muted-foreground">{fun.weather.city}</span></Chip>}
        {w.mute_counter && <MuteSetter days={daysSince(fun.mute.reset_at) ?? 0} onSaved={load} />}
        {w.tea_round && (
          <button type="button" onClick={spinWheel} className="transition-transform hover:scale-105" data-testid="fun-random-wheel">
            <Chip icon={Disc3}><span className="text-muted-foreground">Random Wheel:</span> <b>{fun.tea_round?.name || "tap to spin"}</b></Chip>
          </button>
        )}
        {wheel}
        {w.joke && fun.joke && <Chip icon={Quote} testid="fun-joke" className="max-w-full whitespace-normal"><span className="italic">{fun.joke}</span></Chip>}
      </div>
    </div>
  );
};
